import { useCallback, useState } from 'react';
import { mobilePrefsStore, usePaneParams } from './mobilePrefsStore';

function readActivePane(raw: unknown): string {
  if (typeof raw !== 'object' || raw === null) return '';
  const value = (raw as Record<string, unknown>).activePane;
  return typeof value === 'string' ? value : '';
}

/**
 * Hub sub-pane selection persisted under one mobile-prefs pane id.
 *
 * Hydration is read-only: an unknown or stale stored id falls back to
 * `fallback` and is never written back, so a pane that returns later keeps
 * the user's old choice. Only a user selection writes.
 */
export function useHubPane<T extends string>(
  paneId: string,
  valid: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const raw = usePaneParams(paneId);
  const stored = readActivePane(raw);
  // Empty string sentinel: an invalid stored value produces no seed at all,
  // so the fallback is never persisted over it.
  const seedKey = (valid as readonly string[]).includes(stored) ? (stored as T) : '';

  const [active, setActive] = useState<T>(seedKey === '' ? fallback : seedKey);
  // Tracks the last seedKey applied to `active`, so a change in seedKey
  // across renders can be detected and re-seeded without an effect (React's
  // "adjust state during render" pattern — an effect would apply the update
  // one render late and trip react-hooks/set-state-in-effect for no benefit
  // here, since prefs arriving after first paint is exactly this case).
  const [appliedSeed, setAppliedSeed] = useState<T | ''>(seedKey);
  if (seedKey !== appliedSeed) {
    setAppliedSeed(seedKey);
    if (seedKey !== '') setActive(seedKey);
  }

  const select = useCallback(
    (next: T) => {
      setActive(next);
      // Pass only the changed key — the store deep-merges internally.
      mobilePrefsStore.setPaneParams(paneId, { activePane: next });
    },
    [paneId],
  );

  return [active, select];
}
